import cn from 'classnames';
import React, { useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Slider, { Settings } from 'react-slick';

import getProductPreviews from 'store/product/selector/getProductPreviews';
import getProductItem from 'store/product/selector/getProductItem';
import getForcedProductPreviews from 'store/product/selector/getForcedProductPreviews';
import getProductPreviewIndex from 'store/product/selector/getProductPreviewIndex';

import StudioArrow from 'components/Common/Carousel/StudioRightArrow/StudioArrow';
import styles from './ProductPreviewDesktop.module.scss';
import OpLightbox from '../../Common/op-ui-wrappers/OpLightbox/OpLightbox';

const POSTER_PRODUCT_TYPE_IDS = [
  173, // STUDIO SELECTION POSTER
];

const PREVIEWS_SLIDER_SETTINGS: Settings = {
  accessibility: false,
  arrows: false,
  infinite: false,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 3,
};

const MAIN_PREVIEW_SLIDER_SETTINGS: Settings = {
  accessibility: false,
  arrows: true,
  infinite: true,
  speed: 500,
  dots: false,
  slidesToShow: 1,
  slidesToScroll: 1,
};

export function CustomNextArrow(props: any) {
  const { className, style, onClick } = props;
  return <div className={className} style={{ ...style, display: 'block', right: 0, zIndex: 100 }} onClick={onClick} />;
}

export function CustomPrevArrow(props: any) {
  const { className, style, onClick } = props;
  return <div className={className} style={{ ...style, display: 'block', left: 0, zIndex: 100 }} onClick={onClick} />;
}

const ProductPreviewDesktop = () => {
  const originalPreviews = useSelector(getProductPreviews);
  const verticalSlider = useRef(null);
  const horizontalSlider = useRef(null);
  const slideIndex = useRef(0);
  const forcedPreviews = useSelector(getForcedProductPreviews);
  const productPreviewIndex = useSelector(getProductPreviewIndex);
  const currentProductItem = useSelector(getProductItem);
  const mixedPreviews = originalPreviews.map((preview, index) => forcedPreviews[index] || preview);
  const isPoster = POSTER_PRODUCT_TYPE_IDS.includes(currentProductItem?.product.productTypeId || -1);
  const setGreyBackground = !forcedPreviews[0] && isPoster;
  const [activePreview, setActivePreview] = useState<number>(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const gotoSlide = (slideIdx: number) => {
    if (slideIndex.current === slideIdx) {
      return;
    }
    horizontalSlider.current && (horizontalSlider.current as any).slickGoTo(slideIdx);
    verticalSlider.current && (verticalSlider.current as any).slickGoTo(slideIdx);
    slideIndex.current = slideIdx;
    setActivePreview(slideIdx);
  };

  useEffect(() => {
    // if current slide index point to not available preview, slide to first slide
    if (originalPreviews.length && slideIndex.current > originalPreviews.length - 1) {
      gotoSlide(0);
    }
  }, [originalPreviews]);

  useEffect(() => {
    gotoSlide(productPreviewIndex);
  }, [productPreviewIndex]);

  return (
    <div className={styles.productPreviewContainer}>
      <OpLightbox
        images={mixedPreviews.map((preview) => ({
          src: preview,
        }))}
        visible={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        zoomEnabled={false}
      />
      <div className={styles.productPreviews}>
        {mixedPreviews.length > 0 && (
          <Slider
            ref={horizontalSlider}
            afterChange={(currentSlideIdx) => gotoSlide(currentSlideIdx)}
            arrows={MAIN_PREVIEW_SLIDER_SETTINGS.arrows}
            nextArrow={<StudioArrow direction="right" />}
            prevArrow={<StudioArrow direction="left" />}
            accessibility={MAIN_PREVIEW_SLIDER_SETTINGS.accessibility}
            speed={MAIN_PREVIEW_SLIDER_SETTINGS.speed}
            slidesToShow={MAIN_PREVIEW_SLIDER_SETTINGS.slidesToShow}
            slidesToScroll={MAIN_PREVIEW_SLIDER_SETTINGS.slidesToScroll}
            infinite={MAIN_PREVIEW_SLIDER_SETTINGS.infinite}
          >
            {mixedPreviews.map((preview, index) => (
              <div key={`hs_${index}`}>
                <div
                  onClick={() => setIsLightboxOpen(true)}
                  className={cn(styles.productPreview, {
                    [styles.previewSm]: index === 0 && isPoster,
                    [styles.previewShadow]: index === 0 && isPoster && setGreyBackground,
                    [styles.previewGreyBg]: setGreyBackground,
                  })}
                >
                  <img src={preview} alt="" />
                </div>
              </div>
            ))}
          </Slider>
        )}
      </div>
      <div className={styles.productThumbnails}>
        <Slider
          ref={verticalSlider}
          accessibility={PREVIEWS_SLIDER_SETTINGS.accessibility}
          arrows={PREVIEWS_SLIDER_SETTINGS.arrows}
          infinite={PREVIEWS_SLIDER_SETTINGS.infinite}
          speed={PREVIEWS_SLIDER_SETTINGS.speed}
          slidesToShow={PREVIEWS_SLIDER_SETTINGS.slidesToShow}
          slidesToScroll={PREVIEWS_SLIDER_SETTINGS.slidesToScroll}
        >
          {mixedPreviews.map((preview, index) => (
            <div key={`vs_${index}`} onClick={() => gotoSlide(index)}>
              <div
                className={cn(styles.productThumbnail, {
                  [styles.previewSm]: index === 0 && isPoster,
                  [styles.previewShadow]: index === 0 && isPoster && setGreyBackground,
                  [styles.previewGreyBg]: setGreyBackground,
                  [styles.selected]: index === activePreview,
                })}
              >
                <img itemProp="image" src={preview} alt="" />
              </div>
            </div>
          ))}
        </Slider>
      </div>
    </div>
  );
};

export default ProductPreviewDesktop;
